import { createContext, Dispatch, useEffect, useReducer } from 'react';
import { useRouter } from 'next/router';
import { mapValues, pickBy } from 'lodash-es';

export const QsContext = createContext<
  [Record<string, string>, Dispatch<Partial<Record<string, string>>>]
>([] as any);

export const useQs = <U extends string>() => {
  type T = Record<U, string>;

  const router = useRouter();
  const getQs = () =>
    mapValues(router.query, (v) => (Array.isArray(v) ? v.join('|') : v)) as T;

  const [qs, updateQs] = useReducer((_: T, newQs: Partial<T>) => {
    const formatedNewQs: Partial<T> = mapValues(newQs, (v) =>
      v?.replace(/^\|/, '').replace(/\|$/, ''),
    ); // clean the data to looks better in the URL)
    const nextQs: T = { ...getQs(), ...formatedNewQs };
    const cleanQs = pickBy(nextQs, (e) => e) as T; // remove undefined from query string

    return cleanQs;
  }, getQs());

  const getCurrentlyChangedFilter = (newQs: Record<string, string>) => {
    const currentFilters: Record<string, string> = getQs();
    const allQsProps: string[] = Array.from(
      new Set([...Object.keys(newQs), ...Object.keys(currentFilters)]),
    );

    const [filter = null] = allQsProps.filter(
      (key) => key !== 'query' && newQs[key] !== currentFilters[key],
    );

    if (filter === 'cpu' || filter === 'ram') return filter.toUpperCase();

    const filterTitle = filter
      ? `{${filter.charAt(0).toUpperCase() + filter.slice(1)}`
          .match(/[A-Z][a-z]*/g)
          ?.join(' ')
      : '';

    return filterTitle;
  };

  const setQs = async (newQs: T) => {
    const [path, currentQs] = router.asPath.split('?');
    const nextQs = new URLSearchParams(newQs).toString();

    if (currentQs === nextQs) return;
    const filterSegment = getCurrentlyChangedFilter(newQs);

    if (filterSegment === 'Categories' && currentQs === undefined) return;

    await router.replace(`${path}?${nextQs}`, undefined, { scroll: false });
  };

  useEffect(() => {
    void setQs(qs);
  }, [qs]);

  return [qs, updateQs] as const;
};
